import { MetricPerformanceData } from '@cmps/ng-kit';

import { AuthService } from '../auth/auth.service';
import { epicDependencies } from '../store';
import { createReducer } from '../store.model';

import { getPerformanceHeaderData } from './functions/get-performance-card-header-data.function';
import { performancePillars } from './functions/get-performance-pillars.function';
import { getUUID } from './functions/get-uuid.function';
import { prepareMetricPerformanceData } from './functions/metric-performance-data.function';
import { PerformancePillar } from './models/performance-pillar.model';
import { UserPerformanceData } from './models/user-performance-data.model';
import {
    ActionType,
    ReloadResultAction,
    RootClearStateAction,
    RootReloadAction,
    SelectActionAction,
} from './my-performance-card.action';
import {
    defaultState,
    PerformanceCardState,
} from './my-performance-card.state';
import { handleGetPerformanceDataResult } from './state/handle-performance-data.reducer';
import { handleSelectPillarResult } from './state/handle-select-pillar.reducer';
import { GetMetricPerformanceDataResultAction } from './state/metric-performance-data.action';
import { handleMetricPerformanceDataResult } from './state/metric-performance-data.reducer';
import {
    GetPerformanceDataAction,
    GetPerformanceDataResultAction,
} from './state/performance-data.action';
import { TogglePillarCollapseAction } from './state/toggle.action';
import { handleTogglePillarResult } from './state/toggle.reducer';

const { builder, reducer } = createReducer<ActionType, PerformanceCardState>(
    defaultState(),
);

builder
    .add<RootReloadAction>(
        ActionType.MY_PERFORMANCE_CARD__ROOT__RELOAD,
        (state) => {
            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: true,
                    hasData: true,
                },
            };
        },
    )

    .add<ReloadResultAction>(
        ActionType.MY_PERFORMANCE_CARD__ROOT__RELOAD_RESULT,
        (state, action) => {
            const card = action.payload.performanceCard;
            const userId = action.payload.userId;
            const triggeredFromRoot = action.payload.triggeredFromRoot;

            const authService =
                epicDependencies.injector.get<AuthService>(AuthService);
            const userName = `${authService.user.firstName} ${authService.user.lastName}`;

            if (card == null) {
                return {
                    ...state,
                    pending: {
                        ...state.pending,
                        reload: false,
                        hasData: false,
                    },
                };
            }

            const pillarsData: PerformancePillar[] = [];

            const totalPerformancePillar = {
                id: getUUID(),
                isCollapsed: false,
                parentPillar: performancePillars(card.total),
                childPillars: [],
            };

            card.pillars.forEach((pillar) => {
                const parentPillar = performancePillars(pillar);

                const childPillars: MetricPerformanceData[] =
                    pillar.colourGroups.flatMap((group) =>
                        group.metrics.map((metric) =>
                            prepareMetricPerformanceData(metric, false),
                        ),
                    );

                const pillarData: PerformancePillar = {
                    id: getUUID(),
                    isCollapsed: false,
                    parentPillar,
                    childPillars,
                };

                pillarsData.push(pillarData);
            });

            const headerData = getPerformanceHeaderData(card, userName);

            const newUserPerformanceData: UserPerformanceData = {
                totalPerformancePillar,
                pillarsData,
                metricPerformanceData: [],
                performanceCard: card,
                isLoggedIn: triggeredFromRoot,
            };

            return {
                ...state,
                pending: {
                    ...state.pending,
                    reload: false,
                    hasData: true,
                },
                headerData,
                userData: {
                    ...state.userData,
                    [userId]: newUserPerformanceData,
                },
            };
        },
    );

builder
    .add<SelectActionAction>(
        ActionType.MY_PERFORMANCE_CARD_SELECT_PILLAR_ACTION,
        handleSelectPillarResult,
    )
    .add<GetPerformanceDataAction>(
        ActionType.MY_PERFORMANCE_CARD__GET_PERFORMANCE_DATA,
        (state, action) => {
            const { userId, metricId, queueId } = action.payload;
            const key = `${userId}-${metricId ?? ''}-${queueId ?? ''}`;
            if (metricId == null) {
                return {
                    ...state,
                };
            }
            return {
                ...state,
                pending: {
                    ...state.pending,
                    trendDataByKey: {
                        ...(state.pending?.trendDataByKey ?? {}),
                        [key]: true,
                    },
                },
            };
        },
    );

builder.add<GetPerformanceDataResultAction>(
    ActionType.MY_PERFORMANCE_CARD__GET_PERFORMANCE_DATA_RESULT,
    handleGetPerformanceDataResult,
);

builder.add<TogglePillarCollapseAction>(
    ActionType.MY_PERFORMANCE_CARD_TOGGLE_PILLAR_COLLAPSE,
    handleTogglePillarResult,
);

builder.add<GetMetricPerformanceDataResultAction>(
    ActionType.MY_PERFORMANCE_CARD__GET_METRIC_PERFORMANCE_DATA_RESULT,
    handleMetricPerformanceDataResult,
);

builder.add<RootClearStateAction>(
    ActionType.MY_PERFORMANCE_CARD_ROOT__CLEAR_STATE,
    () => {
        return defaultState();
    },
);

export const performanceCardReducer = reducer;
